import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { fetchShopifyCustomers, fetchShopifyOrders, type ShopifyOrder } from '@/lib/shopifyAdmin';
import { Loader2, RefreshCw } from 'lucide-react';

type DbClient = {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  notes: string | null;
  visits_count: number;
  last_seen_at: string;
};

type Appt = {
  id: string;
  starts_at: string;
  status: string;
  treatments: any;
  duration_minutes: number;
};

type RxSubmission = {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  status: string;
  created_at: string;
  summary: string | null;
};

type UnifiedClient = {
  email: string; // merge key
  name: string;
  phone: string | null;
  sources: ('appointments' | 'shopify' | 'personal_rx')[];
  appointmentsCount: number;
  ordersCount: number;
  rxCount: number;
  totalSpent: number;
  currency: string;
  lastSeenAt: string;
  dbClientId?: string;
  notes?: string | null;
};

const STATUS_LABELS: Record<string, string> = {
  booked: 'מאושר',
  completed: 'הושלם',
  noshow: 'לא הגיע/ה',
  cancelled: 'בוטל',
};

const RX_STATUS_LABELS: Record<string, string> = {
  new: 'חדש',
  in_review: 'בבדיקה',
  contacted: 'יצרנו קשר',
  done: 'טופל',
};

const SOURCE_LABELS: Record<string, string> = {
  appointments: 'תורים',
  shopify: 'חנות',
  personal_rx: 'רוקחות',
};

export default function AdminClients() {
  const [list, setList] = useState<UnifiedClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<UnifiedClient | null>(null);
  const [history, setHistory] = useState<{
    appts: Appt[];
    orders: ShopifyOrder[];
    rx: RxSubmission[];
  }>({ appts: [], orders: [], rx: [] });
  const [historyLoading, setHistoryLoading] = useState(false);
  const [notes, setNotes] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [dbClientsRes, rxRes, shopifyCustomers] = await Promise.all([
        supabase.from('clients').select('*').limit(2000),
        supabase
          .from('personal_rx_submissions')
          .select('id,name,email,phone,status,created_at')
          .limit(2000),
        fetchShopifyCustomers().catch(() => []),
      ]);

      const merged = new Map<string, UnifiedClient>();

      const upsert = (key: string, partial: Partial<UnifiedClient> & { lastSeenAt: string }) => {
        const k = key.toLowerCase().trim();
        if (!k) return;
        const ex = merged.get(k);
        if (!ex) {
          merged.set(k, {
            email: k,
            name: partial.name ?? k,
            phone: partial.phone ?? null,
            sources: partial.sources ?? [],
            appointmentsCount: partial.appointmentsCount ?? 0,
            ordersCount: partial.ordersCount ?? 0,
            rxCount: partial.rxCount ?? 0,
            totalSpent: partial.totalSpent ?? 0,
            currency: partial.currency ?? 'ILS',
            lastSeenAt: partial.lastSeenAt,
            dbClientId: partial.dbClientId,
            notes: partial.notes,
          });
        } else {
          ex.name = ex.name && ex.name !== ex.email ? ex.name : partial.name ?? ex.name;
          ex.phone = ex.phone ?? partial.phone ?? null;
          ex.sources = Array.from(new Set([...ex.sources, ...(partial.sources ?? [])])) as any;
          ex.appointmentsCount += partial.appointmentsCount ?? 0;
          ex.ordersCount += partial.ordersCount ?? 0;
          ex.rxCount += partial.rxCount ?? 0;
          ex.totalSpent += partial.totalSpent ?? 0;
          if (partial.currency) ex.currency = partial.currency;
          if (partial.lastSeenAt > ex.lastSeenAt) ex.lastSeenAt = partial.lastSeenAt;
          if (!ex.dbClientId && partial.dbClientId) ex.dbClientId = partial.dbClientId;
          if (!ex.notes && partial.notes) ex.notes = partial.notes;
        }
      };

      (dbClientsRes.data as DbClient[] | null)?.forEach((c) => {
        if (!c.email) return;
        upsert(c.email, {
          name: c.name,
          phone: c.phone,
          sources: ['appointments'],
          appointmentsCount: c.visits_count ?? 0,
          lastSeenAt: c.last_seen_at,
          dbClientId: c.id,
          notes: c.notes,
        });
      });

      shopifyCustomers.forEach((c) => {
        if (!c.email) return;
        upsert(c.email, {
          name: c.displayName,
          phone: c.phone,
          sources: ['shopify'],
          ordersCount: parseInt(c.numberOfOrders) || 0,
          totalSpent: parseFloat(c.amountSpent.amount) || 0,
          currency: c.amountSpent.currencyCode,
          lastSeenAt: c.createdAt,
        });
      });

      (rxRes.data as RxSubmission[] | null)?.forEach((r) => {
        if (!r.email) return;
        upsert(r.email, {
          name: r.name,
          phone: r.phone,
          sources: ['personal_rx'],
          rxCount: 1,
          lastSeenAt: r.created_at,
        });
      });

      const arr = Array.from(merged.values()).sort((a, b) =>
        a.lastSeenAt < b.lastSeenAt ? 1 : -1,
      );
      setList(arr);
    } catch (e) {
      toast.error('שגיאה בטעינת לקוחות', {
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((c) =>
      [c.name, c.email, c.phone].some((v) => v?.toLowerCase().includes(q)),
    );
  }, [list, search]);

  const openClient = async (c: UnifiedClient) => {
    setSelected(c);
    setNotes(c.notes ?? '');
    setHistoryLoading(true);
    try {
      const [apptsRes, rxRes, ordersData] = await Promise.all([
        c.dbClientId
          ? supabase
              .from('appointments')
              .select('id,starts_at,status,treatments,duration_minutes')
              .eq('client_id', c.dbClientId)
              .order('starts_at', { ascending: false })
          : Promise.resolve({ data: [] as Appt[] }),
        supabase
          .from('personal_rx_submissions')
          .select('id,name,email,phone,status,created_at,summary')
          .eq('email', c.email)
          .order('created_at', { ascending: false }),
        fetchShopifyOrders(`email:${c.email}`).catch(() => [] as ShopifyOrder[]),
      ]);
      setHistory({
        appts: ((apptsRes.data as any) ?? []) as Appt[],
        rx: ((rxRes.data as any) ?? []) as RxSubmission[],
        orders: ordersData,
      });
    } finally {
      setHistoryLoading(false);
    }
  };

  const saveNotes = async () => {
    if (!selected?.dbClientId) {
      toast.error('שמירת הערות זמינה רק ללקוחות שיש להם רשומת תור');
      return;
    }
    const { error } = await supabase
      .from('clients')
      .update({ notes })
      .eq('id', selected.dbClientId);
    if (error) toast.error(error.message);
    else toast.success('נשמר');
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">לקוחות</h1>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className="h-4 w-4 me-1" /> רענון
        </Button>
      </div>
      <Input
        placeholder="חיפוש לפי שם, אימייל או טלפון…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <div className="grid md:grid-cols-[1fr_1fr] gap-4">
        <Card className="p-0 overflow-hidden">
          <div className="max-h-[70vh] overflow-y-auto divide-y">
            {loading ? (
              <div className="p-6 text-center text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin inline me-2" /> טוען…
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-4 text-sm text-muted-foreground">אין לקוחות.</div>
            ) : (
              filtered.map((c) => (
                <button
                  key={c.email}
                  onClick={() => openClient(c)}
                  className={`w-full text-right p-3 hover:bg-neutral-50 ${
                    selected?.email === c.email ? 'bg-neutral-100' : ''
                  }`}
                >
                  <div className="font-medium">{c.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {c.email} · {c.phone ?? '—'}
                  </div>
                  <div className="flex gap-1 mt-1 flex-wrap">
                    {c.sources.map((s) => (
                      <Badge key={s} variant="outline" className="text-[10px]">
                        {SOURCE_LABELS[s]}
                      </Badge>
                    ))}
                    {c.ordersCount > 0 && (
                      <span className="text-[10px] text-muted-foreground">
                        {c.ordersCount} הזמנות
                      </span>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </Card>

        <Card className="p-5">
          {!selected ? (
            <div className="text-sm text-muted-foreground">בחר/י לקוח כדי לראות פרטים.</div>
          ) : (
            <div className="space-y-4">
              <div>
                <div className="text-lg font-semibold">{selected.name}</div>
                <div className="text-sm text-muted-foreground">
                  {selected.email} · {selected.phone ?? '—'}
                </div>
                <div className="flex gap-3 mt-2 text-xs">
                  <span>{selected.appointmentsCount} תורים</span>
                  <span>{selected.ordersCount} הזמנות</span>
                  <span>{selected.rxCount} פניות רוקחות</span>
                  {selected.totalSpent > 0 && (
                    <span className="font-medium">
                      סך{' '}
                      {selected.totalSpent.toLocaleString('he-IL', {
                        style: 'currency',
                        currency: selected.currency,
                      })}
                    </span>
                  )}
                </div>
              </div>

              {selected.dbClientId && (
                <div>
                  <div className="text-xs uppercase text-muted-foreground mb-1">הערות</div>
                  <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
                  <Button size="sm" className="mt-2" onClick={saveNotes}>
                    שמירת הערות
                  </Button>
                </div>
              )}

              {historyLoading ? (
                <div className="text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin inline me-2" /> טוען היסטוריה…
                </div>
              ) : (
                <>
                  {history.orders.length > 0 && (
                    <Section title="הזמנות חנות">
                      {history.orders.map((o) => (
                        <div key={o.id} className="p-2 text-sm flex justify-between">
                          <div>
                            <div className="font-medium">{o.name}</div>
                            <div className="text-xs text-muted-foreground">
                              {o.lineItems.edges.map((e) => e.node.title).join(', ')}
                            </div>
                          </div>
                          <div className="text-xs text-left">
                            <div className="tabular-nums">
                              {parseFloat(o.totalPriceSet.shopMoney.amount).toLocaleString(
                                'he-IL',
                                {
                                  style: 'currency',
                                  currency: o.totalPriceSet.shopMoney.currencyCode,
                                },
                              )}
                            </div>
                            <div className="text-muted-foreground">
                              {new Date(o.createdAt).toLocaleDateString('he-IL')}
                            </div>
                          </div>
                        </div>
                      ))}
                    </Section>
                  )}

                  {history.appts.length > 0 && (
                    <Section title="היסטוריית תורים">
                      {history.appts.map((a) => (
                        <div key={a.id} className="p-2 text-sm flex justify-between">
                          <div>
                            <div>{new Date(a.starts_at).toLocaleString('he-IL')}</div>
                            <div className="text-xs text-muted-foreground">
                              {Array.isArray(a.treatments) ? a.treatments.join(', ') : ''} ·{' '}
                              {a.duration_minutes} דק׳
                            </div>
                          </div>
                          <span className="text-xs">{STATUS_LABELS[a.status] ?? a.status}</span>
                        </div>
                      ))}
                    </Section>
                  )}

                  {history.rx.length > 0 && (
                    <Section title="פניות רוקחות אישית">
                      {history.rx.map((r) => (
                        <div key={r.id} className="p-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-xs text-muted-foreground">
                              {new Date(r.created_at).toLocaleString('he-IL')}
                            </span>
                            <Badge variant="outline" className="text-[10px]">
                              {RX_STATUS_LABELS[r.status] ?? r.status}
                            </Badge>
                          </div>
                          {r.summary && (
                            <div className="text-xs mt-1 text-muted-foreground line-clamp-2">
                              {r.summary}
                            </div>
                          )}
                        </div>
                      ))}
                    </Section>
                  )}

                  {history.orders.length === 0 &&
                    history.appts.length === 0 &&
                    history.rx.length === 0 && (
                      <div className="text-sm text-muted-foreground">אין היסטוריה.</div>
                    )}
                </>
              )}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs uppercase text-muted-foreground mb-1">{title}</div>
      <div className="divide-y border rounded">{children}</div>
    </div>
  );
}
